<template>
  <LayoutGridComponent
    v-if="data?.id"
  >
    <div
      ref="headerTarget"
      :class="$style.header"
    >
      {{ label || $t('components.core.blocks.stripe.buy', {price: `${price} ${getCurrencySymbol(data?.price?.currency)}`})?.toLowerCase() }}
      <LayoutHorizontalLineComponent />
    </div>

    <div
      :class="$style.info"
    >
      <div>
        {{ data?.name }}
      </div>

      <div
        v-if="price && !soldout"
      >
        {{ price }} {{ getCurrencySymbol(data?.price?.currency) }}
        <span
          v-if="stripeShippingOptions?.length"
        >
          + {{ $t('components.core.blocks.stripe.plusShipping')?.toLowerCase() }}
        </span>
      </div>

      <div v-if="soldout" style="text-decoration-line: underline;">
        {{ $t('components.core.blocks.stripe.soldOut') }}
      </div>

      <div
        :class="$style['purchase-controls']"
        v-if="!soldout"
      >
        <div
          :class="$style['quantity-controls']"
          v-if="maxQuantity > 1"
        >
          <span>
            {{ $t('components.core.blocks.stripe.quantity') }}: {{ quantity }},
          </span>
          <button :class="$style.quantityButton" @click="quantity >= 1 ? quantity-- : null" :disabled="quantity <= 1">
            -
          </button>
          <button :class="$style.quantityButton" @click="quantity < maxQuantity ? quantity++ : null" :disabled="quantity >= maxQuantity">
            +
          </button>
        </div>
      </div>

      <div
        v-if="stripeShippingOptions?.length && !soldout"
        :class="$style['shipping-options']"
      >
        <legend>{{ $t('components.core.blocks.stripe.shippingOptions') }}:</legend>

        <div
          v-for="option in stripeShippingOptions"
          :key="option.id"
          :class="$style['shipping-options-option']"
        >
          <input
            type="radio"
            :id="option.id"
            :value="option.id"
            @input="event => selectedShippingOptionId = option.attributes.product_id"
            :checked="selectedShippingOptionId === option.attributes.product_id ? true : false"
            :class="$style['shipping-options-input']"
          />

          <label :for="option.id">
            <div>
              {{ option.attributes.title }}
            </div>
            <div
              v-if="option.attributes?.countries?.length"
              class="font-size-small"
              >
              ({{option.attributes?.countries?.map(i => i.country).join(', ')}})
            </div>
            <div>
              {{ getPriceForShippingOption(option.attributes.product_id) ? `${getPriceForShippingOption(option.attributes.product_id).toFixed(2)} ${getCurrencySymbol(data?.price?.currency)}` : '' }}
            </div>
          </label>
        </div>
      </div>

      <div
        v-if="!soldout"
        :class="$style['purchase-total']"
      >
        Total: {{ (priceWithShipping * quantity).toFixed(2) }} {{ getCurrencySymbol(data?.price?.currency) }}
      </div>

      <button
        v-if="!soldout"
        :class="$style.button"
        @click="checkout"
      >
        {{ $t('components.core.blocks.stripe.buy') }}
      </button>

      <div
        v-if="hasStripeSuccessQuery"
        :class="$style['shipping-success']"
      >
        {{ $t('components.core.blocks.stripe.thankYouForYourPurchase') }}
      </div>
    </div>

    <div
      :class="$style.content"
      v-if="description"
    >
      <CoreTextComponent
        :string="description"
      />
      <LayoutHorizontalLineComponent />
    </div>
  </LayoutGridComponent>
</template>

<script setup>
const route = useRoute()

const headerTarget = ref(null)

const props = defineProps({
  block: Object
})

const label = computed(() => {
  return i18nGetLocalizedKey(props?.block, 'string')
})

const description = computed(() => {
  return i18nGetLocalizedKey(props?.block, 'richtext')
})

const productId = props?.block?.stripe_product?.data?.attributes?.stripe_product_id

const stripeShippingOptions = props?.block?.stripe_product?.data?.attributes?.stripe_shipping_options?.data || []
const selectedShippingOptionId = ref(stripeShippingOptions?.[0]?.attributes?.product_id || null)
const selectedShippingOption = computed(() => {
  return stripeShippingOptions.find(i => i.attributes?.product_id === selectedShippingOptionId.value) || null
})

const stripeShippingItems = await Promise.all(stripeShippingOptions?.map(async i => {
  if (!i?.attributes?.product_id) return null
  const data = await $fetch('/api/get-stripe-product', { body: { productId: i.attributes?.product_id }, method: 'POST' })
  return data
}))

const selectedShippingItem = computed(() => {
  return stripeShippingItems.find(i => i.id === selectedShippingOptionId.value) || null
})

const getPriceForShippingOption = (productId) => {
  const option = stripeShippingItems.find(i => i.id === productId)
  if (!option?.price?.unit_amount) return undefined
  return option.price.unit_amount / 100
}

//
const { data } = await useAsyncData(() =>
  $fetch('/api/get-stripe-product', { body: { productId: productId }, method: 'POST' })
)

//
const { data: paymentsData, refresh: refreshPaymentsData } = useAsyncData(async () =>
  await $fetch('/api/get-stripe-payments-for-product-id', { body: { productId: productId, price: data?.value?.price?.unit_amount }, method: 'POST' })
)

useIntervalFn(() => {
  refreshPaymentsData()
}, 10000)

//
const soldout = computed(() => {
  if (!props?.block?.stripe_product?.data?.attributes?.products_available) return false
  if (!paymentsData?.value) return false
  const totalSold = paymentsData?.value?.totalSold || 0
  return totalSold >= (props?.block?.stripe_product?.data?.attributes?.products_available)
})

//
const quantity = ref(1)
const maxQuantity = computed(() => {
  if (props?.block?.stripe_product?.data?.attributes?.max_quantity) {
    const itemsSold = paymentsData?.value?.totalSold || 0
    const available = props?.block?.stripe_product?.data?.attributes?.products_available - itemsSold
    return Math.min(props?.block?.stripe_product?.data?.attributes?.max_quantity, available)
  } else {
    return 1
  }
})

//
const price = computed(() => {
  if (!data?.value?.price?.unit_amount) return null
  return (data?.value?.price?.unit_amount / 100 ).toFixed(2)
})

const priceWithShipping = computed(() => {
  const shippingPrice = getPriceForShippingOption(selectedShippingOptionId.value) || 0
  if (!data?.value?.price?.unit_amount) return null
  return (data?.value?.price?.unit_amount / 100 + shippingPrice).toFixed(2)
})

const getCurrencySymbol = (currency) => {
  if (!currency) return ''
  const symbols = {
    USD: 'dollar',
    EUR: 'euro',
    GBP: 'pound',
    JPY: 'yen',
  }
  return symbols[currency?.toUpperCase()] || currency
}

const checkout = async () => {
  const pageTitle = document?.querySelector('title')?.innerText
  await useFetch(`/api/create-checkout-session`,
    {
      body: {
        quantity: quantity.value,
        product: data?.value,
        shippingProduct: selectedShippingItem.value,
        allowedCountries: selectedShippingOption.value?.attributes?.countries?.map(i => i.country)?.filter(Boolean) || [],
        successUrl: `${window.location.href}?stripe=success`,
        returnUrl: `${window.location.href}?stripe=cancel`,
        pageTitle: pageTitle
      },
      method: 'POST'
  }).then(response => {
    if (response?.data.value?.session_url) {
      window.location.href = response?.data.value?.session_url
    } else {
      alert("Something went wrong. Please contact us via email.")
    }
  }).catch((e) => {
    alert("Something went wrong. Please contact us via email.")
  })
}

const hasStripeSuccessQuery = computed(() => {
  return route?.query.stripe?.includes('success')
})

onMounted(() => {
  setTimeout(() => {
    if (route?.query.stripe) {
      headerTarget.value?.scrollIntoView({ behavior: 'smooth' })
    }
  }, 500)
})
</script>

<style module lang="scss">

.header {
  composes: container--medium from global;
  grid-column: 1 / -1;

  display: flex;
  flex-direction: column;
  gap: var(--unit--spacer);
  text-align: center;

  text-transform: uppercase;
}

.info {
  composes: container--medium from global;
  grid-column: 1 / -1;
}

.button {
  composes: reset-button from global;
  text-transform: uppercase;
  text-decoration-line: underline;
}

.button:hover {
  text-decoration-line: none;
}

.content {
  padding-left: var(--unit--horizontal--tripple);
  padding-right: var(--unit--horizontal--tripple);

  display: flex;
  flex-direction: column;
  gap: var(--unit--spacer);

  text-align: center;

  font-family: 'routed-gothic';

  grid-column: 2 / -1;

  @media (max-width: $breakpoint-xl) {
    grid-column: 1 / -1;
  }
}

.purchase-controls {
  display: flex;
  flex-direction: row;
  gap: var(--unit--horizontal);
}

.quantity-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--unit--spacer);
}

.quantityButton {
  composes: reset-button from global;
  user-select: none;
}

.quantityButton:not([disabled]):hover {
  text-decoration-line: underline;
}

.quantityButton[disabled] {
  cursor: not-allowed;
}

.shipping-options {
  composes: font-size-small from global;
  margin-top: var(--unit--vertical);
}

.shipping-select {
  display: block;
  appearance: none;

  background: transparent;

  border: var(--stroke--default) solid currentColor;

  font-size: inherit;
  line-height: inherit;
  font-family: inherit;
  overflow: visible;

  padding: var(--unit--spacer) var(--unit--horizontal);
}

.shipping-options-option {
  display: flex;
  gap: var(--unit--spacer);
  cursor: pointer;
}

.shipping-options-option input {
  visibility: hidden;
  position: absolute;
  width: 0;
  height: 0;
}

.shipping-options-option:hover div,
.shipping-options-option:has(input:checked) div {
  color: var(--color--pink);
  cursor: pointer;
}

.shipping-success {
  margin-top: var(--unit--vertical);
}

.purchase-total {
  margin-top: var(--unit--vertical);
}
</style>
